import type { PetSpeciesID, PetState } from '../pet';
import { getJSTDate } from '../utils/time';
import { duration } from '../utils/duration';
import RawData from '../data/TextMessages.json';
import { assertNever } from '../utils';
import {
  TextMessages,
  TextMessageItem,
  Time,
} from 'src/replicant/data/TextMessages.schema';
import { tuple } from '../utils/types';

const Data = RawData as TextMessages;

export const petStatusNotifications = tuple(
  'sick',
  'tooth_decay',
  'scared',
  'evolving',
  'very_dirty',
  'very_hungry',
  'very_lonely',
  'very_pee',
  'dirty',
  'hungry',
  'lonely',
  'pee',
  'dead',
  'dying',
  'want_to_go_out',
);

export type PetStatusNotification = (typeof petStatusNotifications)[number];

export type PetMessage = TextMessageItem;

export type PetChatTime = Time;

function isPetStatusNotification(
  status: PetState,
): status is PetStatusNotification {
  switch (status) {
    case 'dead':
    case 'evolving':
    case 'dying':
    case 'dirty':
    case 'hungry':
    case 'lonely':
    case 'pee':
    case 'scared':
    case 'sick':
    case 'tooth_decay':
    case 'very_dirty':
    case 'very_hungry':
    case 'very_lonely':
    case 'very_pee':
      return true;

    case 'want_to_go_out':
    case 'want_to_add_friend':
    case 'happy':
    case 'normal':
    case 'sad':
    case 'sleeping':
    case 'very_sad':
      return false;

    default:
      assertNever(status);
  }
}

export function canSendPetStatusNotification(
  receiveMessagesSetting: 'on' | 'less' | 'off',
  status: PetState,
): status is PetStatusNotification {
  if (receiveMessagesSetting === 'off') {
    // Notifications disabled, bail.
    return false;
  }

  if (!isPetStatusNotification(status)) {
    // No notification for this status
    return false;
  }

  // Do not send all status messages for 'less' setting.
  if (
    ['dirty', 'hungry', 'lonely', 'pee'].includes(status) &&
    receiveMessagesSetting === 'less'
  ) {
    return false;
  }

  return true;
}

export const petMessages = Data.reduce(
  (acc, item) => {
    acc[item.id] = item;
    return acc;
  },
  {} as Record<PetMessage['id'], PetMessage>,
);

export const petSpeciesMessages = Data.reduce(
  (acc, item) => {
    const species = item.pet_id as PetSpeciesID;

    if (!acc[species]) {
      acc[species] = [];
    }
    const messages = acc[species] as PetMessage[];

    messages.push(item);

    return acc;
  },
  {} as { [Species in PetSpeciesID]: PetMessage[] },
);

const petChatScheduleHours = {
  morning: 7,
  lunch: 12,
  evening: 20,
};

export function getNextChatMessageSchedule(now: number, random: number) {
  const date = getJSTDate(now);
  const jstNow = date.getTime();
  const hours = date.getHours();
  date.setMinutes(0, 0, 0);
  const startOfThisHour = date.getTime();
  const partOfHour = jstNow - startOfThisHour;

  let period: PetChatTime;
  if (hours >= petChatScheduleHours.evening) {
    period = 'morning';
  } else if (hours >= petChatScheduleHours.lunch) {
    period = 'evening';
  } else {
    period = 'lunch';
  }

  let startHours = petChatScheduleHours[period] - hours;
  if (period === 'morning') {
    startHours += 24;
  }
  let delayInMS = duration({ hours: startHours }) - partOfHour;
  delayInMS += random * duration({ hours: 1 });

  return { period, delayInMS };
}
